import { RelativeURLBuilder } from '@bigcommerce/atom-utils';
import {
  IProductModel,
  MultiBuy,
  ProductTag,
} from '@masterofmalt/atom.componentlibrary/models/internal/product/IProductModel';

import { getMockIProductModel } from '@/vibes/mom/sections/faceted-product-list/utils';
import { getPriceFromAlgoliaHit } from '~/lib/algolia/get-price';
import { hitSchema } from '~/lib/algolia/types/hit';
import { env } from '~/lib/atom/env';
import { checkFlashSaleIsActive } from '~/lib/atom/flash-sale';
import { getEligiblePromotions } from '~/lib/atom/promotions';
import { ActivePromotion } from '~/lib/atom/types/active-promotion';
import { PriceConfig } from '~/lib/atom/types/price-config';

import { AvailabilityStatus } from '../atom/types/availability-status';
import { mergeVariantAndProductTitle } from '../string-utils';

// eslint-disable-next-line complexity
export const mapToIProductModel = (
  rawHit: unknown,
  priceConfig: PriceConfig,
  promotions: ActivePromotion[] = [],
): IProductModel => {
  const hit = hitSchema.parse(rawHit);

  const price = getPriceFromAlgoliaHit({
    prices: hit.atom_prices,
    ...priceConfig,
  });

  let discountStart: Date | null = null;
  let discountEnd: Date | null = null;

  const flashSaleData = hit.variant_metafields?.['atom.product']?.['flash-sale'];

  if (
    env.NEXT_PUBLIC_SITE_TYPE === 'D2C' &&
    priceConfig.priceListId === env.NEXT_PUBLIC_DEFAULT_PRICE_LIST_ID &&
    price.discountType !== 'NO_DISCOUNT'
  ) {
    if (hit.variant_metafields?.['atom.product']?.['has-retail-price-drop']) {
      price.discountType = 'PRICE_DROP';
    }

    if (flashSaleData) {
      if (checkFlashSaleIsActive(flashSaleData)) {
        price.discountType = 'LIGHTNING_DEAL';
        discountStart = flashSaleData.flashSaleStartDate || null;
        discountEnd = flashSaleData.flashSaleEndDate || null;
      }
    }
  }

  const url = RelativeURLBuilder.Url(hit.url).setQueryParam('sku', hit.sku).toString();

  const eligiblePromotions = getEligiblePromotions(
    parseInt(hit.objectID, 10),
    hit.category_ids || [],
    promotions,
  );

  const multiBuy: MultiBuy[] = [];
  const tags: ProductTag[] = [];

  if (hit.is_best_seller) {
    tags.push('BEST_SELLER');
  }

  if (hit.variant_metafields?.['atom.product']?.['is-staff-pick']) {
    tags.push('STAFF_PICK');
  }

  if (
    hit.variant_metafields?.['atom.product']?.['has-free-delivery'] &&
    priceConfig.countryCode === 'GB' &&
    env.NEXT_PUBLIC_SITE_TYPE === 'D2C'
  ) {
    tags.push('FREE_SHIPPING');
  }

  if (eligiblePromotions.length > 0) {
    const promotion = eligiblePromotions[0];

    if (promotion) {
      const multibuy: MultiBuy = {
        id: promotion.id,
        shortName: promotion.display_name,
        relativeLink: '/',
        description: '',
        startDate: null,
        endDate: null,
      };

      multiBuy.push(multibuy);
      tags.push('MULTI_BUY');
    }
  }

  const optionName = hit.options?.Option;

  let availabilityStatus: AvailabilityStatus = 'Unavailable';

  if (hit.in_stock) {
    if (hit.variant_metafields?.['atom.product']?.['is-preorder'] === 1) {
      availabilityStatus = 'Preorder';
    } else {
      availabilityStatus = 'Available';
    }
  }

  // TODO: This is a mock implementation. Once we have the real data remove getMockIProductModel.
  return getMockIProductModel({
    id: parseInt(hit.sku, 10),
    imageUrlRelative: hit.image_url || '/images/default-product.png',
    urlRelative: url,
    name: optionName ? mergeVariantAndProductTitle(hit.name, optionName) : hit.name,
    shortName: hit.product_metafields?.['atom.product']['short-name'] || '',
    releaseDate: hit.release_date ? new Date(hit.release_date * 1000) : null,
    description: '',
    price: {
      current: price.currentPrice,
      discountedFrom: price.discountedFromPrice,
      discountType: price.discountType,
      discountStart,
      discountEnd,
      isDailyDeal: flashSaleData?.isDailyDeal ?? false,
    },
    meta: {
      abv: hit.abv,
      volume: hit.volume,
      // As part of replatforming this was poorly renamed, it should have been called:
      // `onlyShowBuyButtonOnPdp` in which case we need to invert this.
      showBuyButton: !hit.has_buy_button,
      hasBespokePrice: hit.atom_has_bespoke_price?.[priceConfig.priceListId] ?? false,
      bottler: {
        name: hit.product_metafields?.['atom.product'].bottler || '',
        type: 'bottler',
        urlRelative: '', // We don't have this in search
      },
      distillery: {
        name: hit.product_metafields?.['atom.product'].distillery || '',
        type: 'distillery',
        urlRelative: '', // We don't have this in search
      },
    },
    tags,
    multiBuy,
    reviews: {
      rating: hit.atom_average_rating,
      count: hit.atom_rating_count || 0,
    },
    category: {
      type: {
        name: hit.product_metafields?.['atom.product'].category || '',
        type: 'type',
      },
      style: {
        name: hit.product_metafields?.['atom.product'].style || '',
        type: 'style',
      },
    },
    availability: {
      inStock: hit.in_stock,
      freeStock: 0,
      unlimitedStock: false,
      maxNumberPerCustomer: null,
      expectedDate: null,
    },
    bigCommerce: {
      optionEntityId: null,
      optionValueEntityId: null,
      sku: hit.sku,
      availabilityV2: {
        status: availabilityStatus,
      },
      availability: {
        hasVariantInventory: false,
      },
    },
  });
};
